import { useEffect, useState } from "react";
import { doc, onSnapshot } from "firebase/firestore";
import { useAuth } from "@/contexts/AuthContext";
import { COLLECTIONS } from "@/integrations/firebase/collections";
import { db } from "@/integrations/firebase/firestore";
import { logError } from "@/shared/services/ErrorReporting";

export interface WebBanner {
  enabled: boolean;
  message: string;
}

export interface WebConfig {
  featureFlags: Record<string, boolean>;
  banner: WebBanner;
}

export interface UseWebConfigResult extends WebConfig {
  loading: boolean;
  /** A flag's value, defaulting to enabled when the doc or flag is absent —
   *  shipping behavior must not depend on the config doc existing. */
  flag: (name: string) => boolean;
}

const DEFAULTS: WebConfig = {
  featureFlags: {},
  banner: { enabled: false, message: "" },
};

/** Live client-facing app config (`config/web`): feature flags + the
 *  maintenance banner. Readable by every signed-in user per rules. */
export function useWebConfig(): UseWebConfigResult {
  const { isLoggedIn } = useAuth();
  const [config, setConfig] = useState<WebConfig>(DEFAULTS);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!isLoggedIn) {
      setConfig(DEFAULTS);
      setLoading(false);
      return;
    }
    const unsubscribe = onSnapshot(
      doc(db, COLLECTIONS.CONFIG, "web"),
      (snapshot) => {
        const data = snapshot.data();
        const banner = (data?.banner ?? {}) as Partial<WebBanner>;
        setConfig({
          featureFlags: (data?.featureFlags ?? {}) as Record<string, boolean>,
          banner: {
            enabled: banner.enabled === true,
            message: typeof banner.message === "string" ? banner.message : "",
          },
        });
        setLoading(false);
      },
      (err) => {
        logError(err);
        setConfig(DEFAULTS);
        setLoading(false);
      },
    );
    return () => unsubscribe();
  }, [isLoggedIn]);

  return {
    ...config,
    loading,
    flag: (name: string) => config.featureFlags[name] !== false,
  };
}
